import cn from 'classnames'
import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import {
  type ClipboardEvent,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { SimpleTable } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations'
import { useWindowSize } from 'core-components/hooks/useWindowSize'
import { TVisibilityTrigger } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import { formatSobValue } from 'partner360/lib/metricFormatting'

import { calculateAnnualSobTotal } from '../../../lib/calculateAnnualSobTotal'
import type {
  SobPlanningTableModel,
  SobQuarterKey,
  SobQuarterPolicies,
  SobQuarterValues,
} from '../../../model/types'
import { FloatingNumberInput } from '../floatingNumberInput/FloatingNumberInput'

import { sobActualsTableTranslation } from './translation'

import styles from './SobActualsTable.module.scss'

const OVERALL_SECTION_ANIMATION_MS = 240
const RANGE_TOOLTIP_HIDE_DELAY_MS = 2000
const STACKED_LAYOUT_BREAKPOINT = 1180
const SOB_PERCENT_MIN = 0
const SOB_PERCENT_MAX = 100
const CURRENT_YEAR_VISIBLE_PLANNING_START_INDEX = 5

type RangeTooltipState = {
  inputId: string
  message: string
}

/** `header` puts the title into the empty corner next to the year and quarter headers */
export type SobTableTitlePlacement = 'above' | 'header'

type SobActualsTableProps = {
  model: SobPlanningTableModel
  showOverall: boolean
  /** Hierarchy node name, omitted for a standalone table */
  title?: string
  titlePlacement?: SobTableTitlePlacement
  isNoPlan?: boolean
  noPlanToggleChecked?: boolean
  isNoPlanContractual?: boolean
  showNoPlanToggle?: boolean
  showNoPlanContractualToggle?: boolean
  noPlanToggleDisabled?: boolean
  noPlanContractualToggleDisabled?: boolean
  currentActualsInput: SobQuarterValues
  actualsInput: SobQuarterValues
  planInput: SobQuarterValues
  /** Quarters whose value differs from the loaded one (user edit or recalculation), shown with a highlighted background */
  currentActualsModifiedQuarters?: Partial<Record<SobQuarterKey, boolean>>
  actualsModifiedQuarters?: Partial<Record<SobQuarterKey, boolean>>
  previousYearQuarterPolicies: SobQuarterPolicies
  currentYearQuarterPolicies: SobQuarterPolicies
  onNoPlanChange?: (nextValue: boolean) => void
  onNoPlanContractualChange?: (nextValue: boolean) => void
  onCurrentActualsInputChange: (next: SobQuarterValues) => void
  onActualsInputChange: (next: SobQuarterValues) => void
  onPlanInputChange: (next: SobQuarterValues) => void
}

const PERIOD_COLUMN_CLASSES = [
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  styles.nextYearStartCell,
  undefined,
  undefined,
  undefined,
  undefined,
]
const TOTAL_COLUMN_INDEXES = new Set([4, 9])
const QUARTER_KEYS: readonly SobQuarterKey[] = ['q1', 'q2', 'q3', 'q4']
const PERIOD_LABELS = [
  sobActualsTableTranslation.q1,
  sobActualsTableTranslation.q2,
  sobActualsTableTranslation.q3,
  sobActualsTableTranslation.q4,
  sobActualsTableTranslation.total,
] as const

const toDisplay = (value: number | null) => formatSobValue(value) ?? '—'
const toReadonlyDisplay = (value: number | null) => formatSobValue(value) ?? '—'
const toInput = (value: number | null) => String(value ?? 0)
const isWarningValue = (value: number | null) => value == null || value === 0
const isTotalColumn = (index: number) => TOTAL_COLUMN_INDEXES.has(index)
const isPartialNegativeValue = (value: string) => value.trim().startsWith('-')
const getNextInputValue = (input: HTMLInputElement, insertedValue: string) => {
  const { selectionStart, selectionEnd, value } = input

  if (selectionStart == null || selectionEnd == null) {
    return `${value}${insertedValue}`
  }

  return `${value.slice(0, selectionStart)}${insertedValue}${value.slice(selectionEnd)}`
}
const parseRangeCandidate = (value: string): number | null => {
  const normalized = value.replace(/,/g, '.').replace(/\s/g, '')

  if (normalized === '' || normalized === '.' || normalized === '-.') {
    return null
  }

  const parsed = Number(normalized)

  if (!Number.isFinite(parsed)) {
    return null
  }

  return parsed
}

const parseInput = (value: string): number | null => {
  if (value.trim() === '') {
    return 0
  }

  const normalized = value.replace(',', '.')
  const parsed = Number(normalized)

  if (!Number.isFinite(parsed)) {
    return null
  }

  return parsed
}

const getValueTone = (value: number | null) => {
  if (value == null) {
    return styles.toneNeutral
  }

  if (value >= 0) {
    return styles.tonePositive
  }

  return styles.toneNegative
}

const getAchievementTone = (value: number | null) => {
  if (value == null) {
    return styles.toneNeutral
  }

  if (value >= 100) {
    return styles.tonePositive
  }

  return styles.toneNegative
}

export const SobActualsTable: FC<SobActualsTableProps> = ({
  model,
  showOverall,
  title,
  titlePlacement = 'above',
  isNoPlan = model.isNoPlan,
  noPlanToggleChecked = model.isNoPlan,
  isNoPlanContractual = model.isNoPlanContractual,
  showNoPlanToggle = false,
  showNoPlanContractualToggle = false,
  noPlanToggleDisabled = false,
  noPlanContractualToggleDisabled = false,
  currentActualsInput,
  actualsInput,
  planInput,
  currentActualsModifiedQuarters,
  actualsModifiedQuarters,
  previousYearQuarterPolicies,
  currentYearQuarterPolicies,
  onNoPlanChange,
  onNoPlanContractualChange,
  onCurrentActualsInputChange,
  onActualsInputChange,
}) => {
  const [t] = useTranslation()
  const { width } = useWindowSize()
  const [isOverallExpanded, setIsOverallExpanded] = useState(false)
  const [isOverallExpandedRendered, setIsOverallExpandedRendered] = useState(false)
  const [isOverallRendered, setIsOverallRendered] = useState(showOverall)
  const [isOverallVisible, setIsOverallVisible] = useState(showOverall)
  const [rangeTooltip, setRangeTooltip] = useState<RangeTooltipState | null>(null)
  const isStackedLayout = width <= STACKED_LAYOUT_BREAKPOINT
  const visiblePlanningCells = useMemo(() => model.planningCells.slice(1), [model.planningCells])
  const currentYearPlanningCells = useMemo(
    () => visiblePlanningCells.slice(0, 5),
    [visiblePlanningCells]
  )
  const nextYearPlanningCells = useMemo(
    () => visiblePlanningCells.slice(5, 10),
    [visiblePlanningCells]
  )
  const currentActualsTotal = useMemo(
    () =>
      calculateAnnualSobTotal({
        sob: currentActualsInput,
        sales: model.sob.prevActualsSales,
      }),
    [currentActualsInput, model.sob.prevActualsSales]
  )
  const actualsTotal = useMemo(
    () =>
      calculateAnnualSobTotal({
        sob: actualsInput,
        sales: model.sob.currentActualsSales,
      }),
    [actualsInput, model.sob.currentActualsSales]
  )
  const planTotal = useMemo(
    () =>
      calculateAnnualSobTotal({
        sob: planInput,
        sales: model.sob.currentPlanSales,
      }),
    [model.sob.currentPlanSales, planInput]
  )
  const prevPlanTotal = useMemo(
    () =>
      calculateAnnualSobTotal({
        sob: model.sob.prevPlan,
        sales: model.sob.prevPlanSales,
      }),
    [model.sob.prevPlan, model.sob.prevPlanSales]
  )
  const getEditableInputClassName = (
    value: number | null,
    shouldShowWarning: boolean,
    isModified: boolean
  ) =>
    cn(styles.input, {
      [styles.inputWarning]: shouldShowWarning && isWarningValue(value),
      [styles.inputModified]: isModified,
    })
  const updateQuarterValue = (
    values: SobQuarterValues,
    quarterKey: SobQuarterKey,
    nextValue: number | null
  ): SobQuarterValues => ({
    ...values,
    [quarterKey]: nextValue,
  })
  const renderCellContent = (content: ReactNode, className?: string) => (
    <div className={cn(styles.cellContent, className)}>{content}</div>
  )
  const renderReadonlyValue = (value: number | null) => toReadonlyDisplay(value)
  const renderNoPlanValue = () => <Translation text={sobActualsTableTranslation.noPlan} span />
  const renderPlanningPlannedValue = (
    cell: (typeof visiblePlanningCells)[number],
    index: number
  ) =>
    isNoPlan && index >= CURRENT_YEAR_VISIBLE_PLANNING_START_INDEX
      ? renderNoPlanValue()
      : cell.planned
  const renderStackedPlanningPlannedValue = (
    sectionKey: string,
    cell: (typeof visiblePlanningCells)[number]
  ) => (isNoPlan && sectionKey === 'next' ? renderNoPlanValue() : cell.planned)
  const renderCurrentYearPlanValue = (value: number | null) =>
    isNoPlan ? renderNoPlanValue() : renderReadonlyValue(value)
  const renderCurrentYearPlanTotal = (value: number | null) =>
    isNoPlan ? renderNoPlanValue() : toDisplay(value)
  const renderNoPlanToggle = ({
    checked,
    onChange,
    disabled = false,
    isContractual = false,
  }: {
    checked: boolean
    onChange?: (nextValue: boolean) => void
    disabled?: boolean
    isContractual?: boolean
  }) => {
    if (!onChange) {
      return null
    }

    return (
      <label
        className={cn(styles.noPlanToggle, {
          [styles.noPlanContractual]: isContractual,
        })}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className={styles.noPlanCheckbox}
        />
        <span>
          <Translation text={sobActualsTableTranslation.noPlanSwitcher} span />
        </span>
      </label>
    )
  }
  const renderTableTitle = () =>
    title && titlePlacement === 'above' ? (
      <div className={styles.tableTitle}>
        <Translation text={title} span />
      </div>
    ) : null
  const renderHeaderTitle = () =>
    title && titlePlacement === 'header' ? (
      <span className={styles.headerTitle}>
        <Translation text={title} span />
      </span>
    ) : null
  const renderSobSectionHeader = () => (
    <div className={styles.sobSectionContent}>
      <span className={styles.sobSectionTitle}>
        <Translation text={sobActualsTableTranslation.sobPercent} span />
      </span>
      {showNoPlanToggle &&
        renderNoPlanToggle({
          checked: noPlanToggleChecked,
          onChange: onNoPlanChange,
          disabled: noPlanToggleDisabled,
        })}
      {showNoPlanContractualToggle &&
        renderNoPlanToggle({
          checked: isNoPlanContractual,
          onChange: onNoPlanContractualChange,
          disabled: noPlanContractualToggleDisabled,
          isContractual: true,
        })}
    </div>
  )
  const renderEditableInput = (
    inputId: string,
    value: number | null,
    onChange: (nextValue: number | null) => void,
    shouldShowWarning = true,
    isModified = false
  ) => (
    <Tooltip
      visibilityTrigger={TVisibilityTrigger.Custom}
      visibilityCallback={() => rangeTooltip?.inputId === inputId}
      disabled={rangeTooltip?.inputId !== inputId}
      tipContent={() => <span>{rangeTooltip?.message ?? ''}</span>}
    >
      <div
        onBlurCapture={() => {
          setRangeTooltip((currentTooltip) =>
            currentTooltip?.inputId === inputId ? null : currentTooltip
          )
        }}
        onKeyDownCapture={(event: KeyboardEvent<HTMLDivElement>) => {
          if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
            return
          }

          const target = event.target

          if (!(target instanceof HTMLInputElement)) {
            return
          }

          const nextValue = getNextInputValue(target, event.key)

          if (isPartialNegativeValue(nextValue)) {
            event.preventDefault()
            setRangeTooltip({
              inputId,
              message: `You cannot enter a value below ${SOB_PERCENT_MIN}.`,
            })
            return
          }

          const nextNumericValue = parseRangeCandidate(nextValue)

          if (nextNumericValue != null && nextNumericValue > SOB_PERCENT_MAX) {
            event.preventDefault()
            setRangeTooltip({
              inputId,
              message: `You cannot enter a value above ${SOB_PERCENT_MAX}.`,
            })
            return
          }

          setRangeTooltip((currentTooltip) =>
            currentTooltip?.inputId === inputId ? null : currentTooltip
          )
        }}
        onPasteCapture={(event: ClipboardEvent<HTMLDivElement>) => {
          const target = event.target

          if (!(target instanceof HTMLInputElement)) {
            return
          }

          const nextValue = getNextInputValue(target, event.clipboardData.getData('text'))

          if (isPartialNegativeValue(nextValue)) {
            event.preventDefault()
            setRangeTooltip({
              inputId,
              message: `You cannot enter a value below ${SOB_PERCENT_MIN}.`,
            })
            return
          }

          const nextNumericValue = parseRangeCandidate(nextValue)

          if (nextNumericValue != null && nextNumericValue > SOB_PERCENT_MAX) {
            event.preventDefault()
            setRangeTooltip({
              inputId,
              message: `You cannot enter a value above ${SOB_PERCENT_MAX}.`,
            })
            return
          }

          setRangeTooltip((currentTooltip) =>
            currentTooltip?.inputId === inputId ? null : currentTooltip
          )
        }}
      >
        <FloatingNumberInput
          floatLength={5}
          hasSpin={false}
          align="right"
          useGrouping={true}
          value={toInput(value)}
          min={SOB_PERCENT_MIN}
          max={SOB_PERCENT_MAX}
          onChange={(nextValue) => {
            setRangeTooltip((currentTooltip) =>
              currentTooltip?.inputId === inputId ? null : currentTooltip
            )
            onChange(parseInput(nextValue))
          }}
          classes={{
            inputClasses: {
              root: getEditableInputClassName(value, shouldShowWarning, isModified),
              content: styles.inputContent,
            },
          }}
          checkFn={ValidationHelper.checkNullablePercent}
          maximumFractionDigits={1}
        />
      </div>
    </Tooltip>
  )
  const renderPreviousYearActualInput = (quarterKey: SobQuarterKey, value: number | null) =>
    renderEditableInput(
      `current-${quarterKey}`,
      value,
      (nextValue) =>
        onCurrentActualsInputChange(updateQuarterValue(currentActualsInput, quarterKey, nextValue)),
      previousYearQuarterPolicies[quarterKey] === 'required',
      currentActualsModifiedQuarters?.[quarterKey] ?? false
    )
  const renderCurrentYearActualInput = (quarterKey: SobQuarterKey, value: number | null) => {
    const policy = currentYearQuarterPolicies[quarterKey]

    if (policy === 'disabled') {
      return renderReadonlyValue(value)
    }

    return renderEditableInput(
      `next-${quarterKey}`,
      value,
      (nextValue) => onActualsInputChange(updateQuarterValue(actualsInput, quarterKey, nextValue)),
      policy === 'required' && !isNoPlan,
      actualsModifiedQuarters?.[quarterKey] ?? false
    )
  }

  useEffect(() => {
    let timeoutId: number | undefined
    let frameId: number | undefined
    let nestedFrameId: number | undefined

    if (showOverall) {
      setIsOverallRendered(true)
      setIsOverallVisible(false)
      setIsOverallExpanded(false)
      frameId = window.requestAnimationFrame(() => {
        nestedFrameId = window.requestAnimationFrame(() => {
          setIsOverallVisible(true)
        })
      })
    } else {
      setIsOverallVisible(false)
      setIsOverallExpanded(false)
      timeoutId = window.setTimeout(() => {
        setIsOverallRendered(false)
      }, OVERALL_SECTION_ANIMATION_MS)
    }

    return () => {
      if (frameId !== undefined) {
        window.cancelAnimationFrame(frameId)
      }

      if (nestedFrameId !== undefined) {
        window.cancelAnimationFrame(nestedFrameId)
      }

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [showOverall])

  useEffect(() => {
    if (isOverallExpanded) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setIsOverallExpandedRendered(false)
    }, OVERALL_SECTION_ANIMATION_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isOverallExpanded])

  useEffect(() => {
    if (!rangeTooltip) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setRangeTooltip(null)
    }, RANGE_TOOLTIP_HIDE_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [rangeTooltip])

  const handleOverallToggle = () => {
    if (isOverallExpanded) {
      setIsOverallExpanded(false)
      return
    }

    setIsOverallExpandedRendered(true)
    setIsOverallExpanded(true)
  }

  const renderPeriodHeaderCells = () =>
    PERIOD_LABELS.map((label, index) => (
      <SimpleTable.HeaderCell
        key={label}
        className={cn(styles.subHeaderCell, {
          [styles.totalHeaderCell]: isTotalColumn(index),
        })}
      >
        <Translation text={label} span />
      </SimpleTable.HeaderCell>
    ))

  const renderStackedOverallRows = (
    sectionKey: string,
    planningCells: typeof visiblePlanningCells
  ) => {
    if (!isOverallRendered) {
      return null
    }

    return [
      <SimpleTable.BodyRow
        key={`${sectionKey}-planning-main`}
        className={cn(styles.overall, {
          [styles.collapsibleRowCollapsed]: !isOverallVisible,
        })}
      >
        <SimpleTable.HeaderCell
          rowSpan={isOverallExpandedRendered && isOverallVisible ? 3 : 1}
          className={styles.overallCell}
        >
          <div
            className={cn(styles.collapsibleContent, {
              [styles.collapsibleContentCollapsed]: !isOverallVisible,
            })}
          >
            <button
              type="button"
              className={styles.sectionToggle}
              onClick={handleOverallToggle}
              aria-expanded={isOverallExpanded && isOverallVisible}
              aria-label={t(
                isOverallExpanded
                  ? sobActualsTableTranslation.collapseOverallSection
                  : sobActualsTableTranslation.expandOverallSection
              )}
            >
              <Translation text={sobActualsTableTranslation.finance} span />
              <span
                className={cn(styles.sectionToggleIcon, {
                  [styles.sectionToggleIconCollapsed]: !isOverallExpanded,
                })}
                aria-hidden
              >
                ▾
              </span>
            </button>
          </div>
        </SimpleTable.HeaderCell>
        <SimpleTable.HeaderCell className={styles.rowLabelCell}>
          <div
            className={cn(styles.collapsibleContent, {
              [styles.collapsibleContentCollapsed]: !isOverallVisible,
            })}
          >
            <span className={styles.primaryLabel}>
              <Translation text={sobActualsTableTranslation.actuals} span />
            </span>
            <span>
              <Translation text={sobActualsTableTranslation.planned} span />
            </span>
          </div>
        </SimpleTable.HeaderCell>

        {planningCells.map((cell, index) => (
          <SimpleTable.BodyCell
            key={`${sectionKey}-planning-main-${index}`}
            className={cn(styles.valueCell, styles.overallToneCell, {
              [styles.totalValueCell]: isTotalColumn(index),
            })}
          >
            <div
              className={cn(styles.collapsibleContent, {
                [styles.collapsibleContentCollapsed]: !isOverallVisible,
              })}
            >
              <span className={styles.primaryValue}>{cell.actual}</span>
              <span>{renderStackedPlanningPlannedValue(sectionKey, cell)}</span>
            </div>
          </SimpleTable.BodyCell>
        ))}
      </SimpleTable.BodyRow>,
      ...(isOverallExpandedRendered
        ? [
            <SimpleTable.BodyRow
              key={`${sectionKey}-planning-yoy`}
              className={cn(styles.overall, {
                [styles.collapsibleRowCollapsed]: !isOverallVisible || !isOverallExpanded,
              })}
            >
              <SimpleTable.HeaderCell className={styles.rowLabelCell}>
                <div
                  className={cn(styles.collapsibleContent, {
                    [styles.collapsibleContentCollapsed]: !isOverallVisible || !isOverallExpanded,
                  })}
                >
                  <span>
                    <Translation text={sobActualsTableTranslation.actualsYoy} span />
                  </span>
                  <span>
                    <Translation text={sobActualsTableTranslation.planVsLyActualsYoy} span />
                  </span>
                </div>
              </SimpleTable.HeaderCell>

              {planningCells.map((cell, index) => (
                <SimpleTable.BodyCell
                  key={`${sectionKey}-planning-yoy-${index}`}
                  className={cn(styles.valueCell, styles.overallToneCell, {
                    [styles.totalValueCell]: isTotalColumn(index),
                  })}
                >
                  <div
                    className={cn(styles.collapsibleContent, {
                      [styles.collapsibleContentCollapsed]: !isOverallVisible || !isOverallExpanded,
                    })}
                  >
                    <span className={getValueTone(cell.actualYoYValue)}>{cell.actualYoY}</span>
                    <span className={getValueTone(cell.planVsLyYoYValue)}>{cell.planVsLyYoY}</span>
                  </div>
                </SimpleTable.BodyCell>
              ))}
            </SimpleTable.BodyRow>,

            <SimpleTable.BodyRow
              key={`${sectionKey}-planning-achievement`}
              className={cn({
                [styles.collapsibleRowCollapsed]: !isOverallVisible || !isOverallExpanded,
              })}
            >
              <SimpleTable.HeaderCell className={styles.rowLabelCell}>
                <div
                  className={cn(styles.collapsibleContent, {
                    [styles.collapsibleContentCollapsed]: !isOverallVisible || !isOverallExpanded,
                  })}
                >
                  <Translation text={sobActualsTableTranslation.planAchievement} span />
                </div>
              </SimpleTable.HeaderCell>

              {planningCells.map((cell, index) => (
                <SimpleTable.BodyCell
                  key={`${sectionKey}-planning-achievement-${index}`}
                  className={cn(styles.valueCell, styles.overallToneCell, {
                    [styles.totalValueCell]: isTotalColumn(index),
                  })}
                >
                  <div
                    className={cn(styles.collapsibleContent, {
                      [styles.collapsibleContentCollapsed]: !isOverallVisible || !isOverallExpanded,
                    })}
                  >
                    <span className={getAchievementTone(cell.achievementValue)}>
                      {cell.achievement}
                    </span>
                  </div>
                </SimpleTable.BodyCell>
              ))}
            </SimpleTable.BodyRow>,
          ]
        : []),
    ]
  }

  const renderStackedSobRows = (
    sectionKey: string,
    yearLabel: string,
    actualValues: SobQuarterValues,
    actualTotal: number | null,
    actualCellRenderer: (
      quarterKey: (typeof QUARTER_KEYS)[number],
      value: number | null
    ) => ReactNode,
    planValues: SobQuarterValues,
    planTotalValue: number | null
  ) => (
    <SimpleTable className={cn(styles.table, styles.stackedTable)}>
      <SimpleTable.HeaderRow>
        <SimpleTable.HeaderCell className={styles.emptyHeader} colSpan={2} rowSpan={2}>
          {sectionKey === 'current' ? renderHeaderTitle() : null}
        </SimpleTable.HeaderCell>
        <SimpleTable.HeaderCell className={styles.headerCell} colSpan={5}>
          {yearLabel}
        </SimpleTable.HeaderCell>
      </SimpleTable.HeaderRow>

      <SimpleTable.HeaderRow>{renderPeriodHeaderCells()}</SimpleTable.HeaderRow>

      {renderStackedOverallRows(
        sectionKey,
        sectionKey === 'current' ? currentYearPlanningCells : nextYearPlanningCells
      )}

      <SimpleTable.BodyRow>
        <SimpleTable.HeaderCell rowSpan={2} className={styles.sobSectionCell}>
          {renderSobSectionHeader()}
        </SimpleTable.HeaderCell>
        <SimpleTable.HeaderCell className={cn(styles.rowLabelCell, styles.sobActualsLabel)}>
          <Translation text={sobActualsTableTranslation.actuals} span />
        </SimpleTable.HeaderCell>

        {QUARTER_KEYS.map((quarterKey) => (
          <SimpleTable.BodyCell
            key={`${sectionKey}-actual-${quarterKey}`}
            className={cn(styles.valueCell, styles.sobActualsToneCell)}
          >
            {renderCellContent(actualCellRenderer(quarterKey, actualValues[quarterKey]))}
          </SimpleTable.BodyCell>
        ))}
        <SimpleTable.BodyCell
          className={cn(
            styles.valueCell,
            styles.primaryValue,
            styles.sobActualsToneCell,
            styles.totalValueCell
          )}
        >
          {renderCellContent(toDisplay(actualTotal), styles.primaryValue)}
        </SimpleTable.BodyCell>
      </SimpleTable.BodyRow>

      <SimpleTable.BodyRow>
        <SimpleTable.HeaderCell className={cn(styles.rowLabelCell, styles.sobPlanLabel)}>
          <Translation text={sobActualsTableTranslation.jbpPlan} span />
        </SimpleTable.HeaderCell>

        {QUARTER_KEYS.map((quarterKey) => (
          <SimpleTable.BodyCell
            key={`${sectionKey}-plan-${quarterKey}`}
            className={cn(styles.valueCell, styles.sobPlanToneCell)}
          >
            {renderCellContent(
              sectionKey === 'next'
                ? renderCurrentYearPlanValue(planValues[quarterKey])
                : renderReadonlyValue(planValues[quarterKey])
            )}
          </SimpleTable.BodyCell>
        ))}
        <SimpleTable.BodyCell
          className={cn(styles.valueCell, styles.sobPlanToneCell, styles.totalValueCell)}
        >
          {renderCellContent(
            sectionKey === 'next'
              ? renderCurrentYearPlanTotal(planTotalValue)
              : toDisplay(planTotalValue)
          )}
        </SimpleTable.BodyCell>
      </SimpleTable.BodyRow>
    </SimpleTable>
  )

  if (isStackedLayout) {
    return (
      <div className={cn(styles.tableShell, styles.stackedShell)}>
        {renderTableTitle()}

        {renderStackedSobRows(
          'current',
          model.years.current,
          currentActualsInput,
          currentActualsTotal,
          renderPreviousYearActualInput,
          model.sob.prevPlan,
          prevPlanTotal
        )}

        {renderStackedSobRows(
          'next',
          model.years.next,
          actualsInput,
          actualsTotal,
          renderCurrentYearActualInput,
          planInput,
          planTotal
        )}
      </div>
    )
  }

  return (
    <div className={styles.tableShell}>
      {renderTableTitle()}

      <SimpleTable className={styles.table}>
        <SimpleTable.HeaderRow>
          <SimpleTable.HeaderCell className={styles.emptyHeader} colSpan={2} rowSpan={2}>
            {renderHeaderTitle()}
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.headerCell} colSpan={5}>
            {model.years.current}
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell
            className={cn(styles.headerCell, styles.nextYearHeader)}
            colSpan={5}
          >
            {model.years.next}
          </SimpleTable.HeaderCell>
        </SimpleTable.HeaderRow>

        <SimpleTable.HeaderRow>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q1} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q2} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q3} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q4} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.total} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={cn(styles.subHeaderCell, styles.nextYearStartCell)}>
            <Translation text={sobActualsTableTranslation.q1} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q2} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q3} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.q4} span />
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={styles.subHeaderCell}>
            <Translation text={sobActualsTableTranslation.total} span />
          </SimpleTable.HeaderCell>
        </SimpleTable.HeaderRow>

        {isOverallRendered
          ? [
              <SimpleTable.BodyRow
                key="planning-main"
                className={cn(styles.overall, {
                  [styles.collapsibleRowCollapsed]: !isOverallVisible,
                })}
              >
                <SimpleTable.HeaderCell
                  rowSpan={isOverallExpandedRendered && isOverallVisible ? 3 : 1}
                  className={styles.overallCell}
                >
                  <div
                    className={cn(styles.collapsibleContent, {
                      [styles.collapsibleContentCollapsed]: !isOverallVisible,
                    })}
                  >
                    <button
                      type="button"
                      className={styles.sectionToggle}
                      onClick={handleOverallToggle}
                      aria-expanded={isOverallExpanded && isOverallVisible}
                      aria-label={t(
                        isOverallExpanded
                          ? sobActualsTableTranslation.collapseOverallSection
                          : sobActualsTableTranslation.expandOverallSection
                      )}
                    >
                      <Translation text={sobActualsTableTranslation.finance} span />
                      <span
                        className={cn(styles.sectionToggleIcon, {
                          [styles.sectionToggleIconCollapsed]: !isOverallExpanded,
                        })}
                        aria-hidden
                      >
                        ▾
                      </span>
                    </button>
                  </div>
                </SimpleTable.HeaderCell>
                <SimpleTable.HeaderCell className={styles.rowLabelCell}>
                  <div
                    className={cn(styles.collapsibleContent, {
                      [styles.collapsibleContentCollapsed]: !isOverallVisible,
                    })}
                  >
                    <span className={styles.primaryLabel}>
                      <Translation text={sobActualsTableTranslation.actuals} span />
                    </span>
                    <span>
                      <Translation text={sobActualsTableTranslation.planned} span />
                    </span>
                  </div>
                </SimpleTable.HeaderCell>

                {visiblePlanningCells.map((cell, index) => (
                  <SimpleTable.BodyCell
                    key={`planning-main-${index}`}
                    className={cn(
                      styles.valueCell,
                      PERIOD_COLUMN_CLASSES[index],
                      styles.overallToneCell,
                      {
                        [styles.totalValueCell]: isTotalColumn(index),
                      }
                    )}
                  >
                    <div
                      className={cn(styles.collapsibleContent, {
                        [styles.collapsibleContentCollapsed]: !isOverallVisible,
                      })}
                    >
                      <span className={styles.primaryValue}>{cell.actual}</span>
                      <span>{renderPlanningPlannedValue(cell, index)}</span>
                    </div>
                  </SimpleTable.BodyCell>
                ))}
              </SimpleTable.BodyRow>,
              ...(isOverallExpandedRendered
                ? [
                    <SimpleTable.BodyRow
                      key="planning-yoy"
                      className={cn(styles.overall, {
                        [styles.collapsibleRowCollapsed]: !isOverallVisible || !isOverallExpanded,
                      })}
                    >
                      <SimpleTable.HeaderCell className={styles.rowLabelCell}>
                        <div
                          className={cn(styles.collapsibleContent, {
                            [styles.collapsibleContentCollapsed]:
                              !isOverallVisible || !isOverallExpanded,
                          })}
                        >
                          <span>
                            <Translation text={sobActualsTableTranslation.actualsYoy} span />
                          </span>
                          <span>
                            <Translation
                              text={sobActualsTableTranslation.planVsLyActualsYoy}
                              span
                            />
                          </span>
                        </div>
                      </SimpleTable.HeaderCell>

                      {visiblePlanningCells.map((cell, index) => (
                        <SimpleTable.BodyCell
                          key={`planning-yoy-${index}`}
                          className={cn(
                            styles.valueCell,
                            PERIOD_COLUMN_CLASSES[index],
                            styles.overallToneCell,
                            {
                              [styles.totalValueCell]: isTotalColumn(index),
                            }
                          )}
                        >
                          <div
                            className={cn(styles.collapsibleContent, {
                              [styles.collapsibleContentCollapsed]:
                                !isOverallVisible || !isOverallExpanded,
                            })}
                          >
                            <span className={getValueTone(cell.actualYoYValue)}>
                              {cell.actualYoY}
                            </span>
                            <span className={getValueTone(cell.planVsLyYoYValue)}>
                              {cell.planVsLyYoY}
                            </span>
                          </div>
                        </SimpleTable.BodyCell>
                      ))}
                    </SimpleTable.BodyRow>,

                    <SimpleTable.BodyRow
                      key="planning-achievement"
                      className={cn({
                        [styles.collapsibleRowCollapsed]: !isOverallVisible || !isOverallExpanded,
                      })}
                    >
                      <SimpleTable.HeaderCell className={styles.rowLabelCell}>
                        <div
                          className={cn(styles.collapsibleContent, {
                            [styles.collapsibleContentCollapsed]:
                              !isOverallVisible || !isOverallExpanded,
                          })}
                        >
                          <Translation text={sobActualsTableTranslation.planAchievement} span />
                        </div>
                      </SimpleTable.HeaderCell>

                      {visiblePlanningCells.map((cell, index) => (
                        <SimpleTable.BodyCell
                          key={`planning-achievement-${index}`}
                          className={cn(
                            styles.valueCell,
                            PERIOD_COLUMN_CLASSES[index],
                            styles.overallToneCell,
                            {
                              [styles.totalValueCell]: isTotalColumn(index),
                            }
                          )}
                        >
                          <div
                            className={cn(styles.collapsibleContent, {
                              [styles.collapsibleContentCollapsed]:
                                !isOverallVisible || !isOverallExpanded,
                            })}
                          >
                            <span className={getAchievementTone(cell.achievementValue)}>
                              {cell.achievement}
                            </span>
                          </div>
                        </SimpleTable.BodyCell>
                      ))}
                    </SimpleTable.BodyRow>,
                  ]
                : []),
            ]
          : null}

        <SimpleTable.BodyRow>
          <SimpleTable.HeaderCell rowSpan={2} className={styles.sobSectionCell}>
            {renderSobSectionHeader()}
          </SimpleTable.HeaderCell>
          <SimpleTable.HeaderCell className={cn(styles.rowLabelCell, styles.sobActualsLabel)}>
            <Translation text={sobActualsTableTranslation.actuals} span />
          </SimpleTable.HeaderCell>

          {QUARTER_KEYS.map((quarterKey, index) => (
            <SimpleTable.BodyCell
              key={`current-${quarterKey}`}
              className={cn(
                styles.valueCell,
                PERIOD_COLUMN_CLASSES[index],
                styles.sobActualsToneCell
              )}
            >
              {renderCellContent(
                renderPreviousYearActualInput(quarterKey, currentActualsInput[quarterKey])
              )}
            </SimpleTable.BodyCell>
          ))}
          <SimpleTable.BodyCell
            className={cn(
              styles.valueCell,
              PERIOD_COLUMN_CLASSES[4],
              styles.primaryValue,
              styles.sobActualsToneCell,
              styles.totalValueCell
            )}
          >
            {renderCellContent(toDisplay(currentActualsTotal), styles.primaryValue)}
          </SimpleTable.BodyCell>

          {QUARTER_KEYS.map((quarterKey, index) => (
            <SimpleTable.BodyCell
              key={`next-${quarterKey}`}
              className={cn(
                styles.valueCell,
                PERIOD_COLUMN_CLASSES[index + 5],
                styles.sobActualsToneCell
              )}
            >
              {renderCellContent(
                renderCurrentYearActualInput(quarterKey, actualsInput[quarterKey])
              )}
            </SimpleTable.BodyCell>
          ))}
          <SimpleTable.BodyCell
            className={cn(
              styles.valueCell,
              PERIOD_COLUMN_CLASSES[9],
              styles.primaryValue,
              styles.sobActualsToneCell,
              styles.totalValueCell
            )}
          >
            {renderCellContent(toDisplay(actualsTotal), styles.primaryValue)}
          </SimpleTable.BodyCell>
        </SimpleTable.BodyRow>

        <SimpleTable.BodyRow>
          <SimpleTable.HeaderCell className={cn(styles.rowLabelCell, styles.sobPlanLabel)}>
            <Translation text={sobActualsTableTranslation.jbpPlan} span />
          </SimpleTable.HeaderCell>

          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[0], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderReadonlyValue(model.sob.prevPlan.q1))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[1], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderReadonlyValue(model.sob.prevPlan.q2))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[2], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderReadonlyValue(model.sob.prevPlan.q3))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[3], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderReadonlyValue(model.sob.prevPlan.q4))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(
              styles.valueCell,
              PERIOD_COLUMN_CLASSES[4],
              styles.sobPlanToneCell,
              styles.totalValueCell
            )}
          >
            {renderCellContent(
              toDisplay(
                calculateAnnualSobTotal({
                  sob: model.sob.prevPlan,
                  sales: model.sob.prevPlanSales,
                })
              )
            )}
          </SimpleTable.BodyCell>

          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[5], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderCurrentYearPlanValue(planInput.q1))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[6], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderCurrentYearPlanValue(planInput.q2))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[7], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderCurrentYearPlanValue(planInput.q3))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(styles.valueCell, PERIOD_COLUMN_CLASSES[8], styles.sobPlanToneCell)}
          >
            {renderCellContent(renderCurrentYearPlanValue(planInput.q4))}
          </SimpleTable.BodyCell>
          <SimpleTable.BodyCell
            className={cn(
              styles.valueCell,
              PERIOD_COLUMN_CLASSES[9],
              styles.sobPlanToneCell,
              styles.totalValueCell
            )}
          >
            {renderCellContent(renderCurrentYearPlanTotal(planTotal))}
          </SimpleTable.BodyCell>
        </SimpleTable.BodyRow>
      </SimpleTable>
    </div>
  )
}
